import { cn } from '@/lib/utils'
import { HTMLAttributes, ReactNode } from 'react'

/**
 * Componentes de marca do design system ODB Promo, os mesmos padrões usados
 * em apps.odbpromo.com.br. Consomem só os tokens de src/globals.css.
 */

/** Rótulo curto acima do título: caixa-alta, 12px, rosa. */
export const Eyebrow = ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
    <p
        className={cn(
            'text-xs font-semibold uppercase tracking-[0.12em] text-brand-pink',
            className
        )}
        {...props}
    />
)

/** Palavra-chave dentro do BrandTitle: teal, peso heavy. */
export const Accent = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => (
    <span className={cn('font-extrabold text-brand', className)} {...props} />
)

/** Título leve em cinza, com 1 ou 2 palavras-chave em <Accent>. */
export const BrandTitle = ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
    <h1
        className={cn(
            'font-display text-2xl font-normal leading-tight tracking-[-0.02em] text-[#404040] dark:text-foreground md:text-3xl',
            className
        )}
        {...props}
    />
)

/** Barra de 64×4px em três blocos sólidos: teal · amarelo · rosa. */
export const AccentBar = ({ className }: { className?: string }) => (
    <div
        aria-hidden
        className={cn('flex h-1 w-16 overflow-hidden rounded-sm', className)}
    >
        <span className="flex-[34] bg-brand" />
        <span className="flex-[33] bg-brand-yellow" />
        <span className="flex-[33] bg-brand-pink" />
    </div>
)

interface PageHeadingProps {
    eyebrow: string
    children: ReactNode
    className?: string
}

/** Cabeçalho de página: eyebrow + título + barra tricolor. */
export const PageHeading = ({ eyebrow, children, className }: PageHeadingProps) => (
    <div className={cn('flex flex-col gap-2', className)}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <BrandTitle>{children}</BrandTitle>
        <AccentBar />
    </div>
)

interface MetricProps {
    label: ReactNode
    value: ReactNode
    help?: string
    className?: string
}

/** Indicador: borda esquerda teal, número em display heavy teal. */
export const Metric = ({ label, value, help, className }: MetricProps) => (
    <div
        title={help}
        className={cn(
            'rounded-md border border-l-[3px] border-border border-l-brand bg-card p-4',
            className
        )}
    >
        <span className="block text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">
            {label}
        </span>
        <b className="mt-1 block font-display text-3xl font-extrabold leading-tight text-brand">
            {value}
        </b>
    </div>
)

const calloutTone = {
    note: 'border-l-brand-yellow bg-warning-50',
    primary: 'border-l-brand bg-accent',
    risk: 'border-l-brand-pink bg-od-secondary-50',
}

interface CalloutProps extends HTMLAttributes<HTMLDivElement> {
    tone?: keyof typeof calloutTone
}

/** Nota em destaque: amarelo (nota), teal (reforço) ou rosa (risco). */
export const Callout = ({ tone = 'note', className, ...props }: CalloutProps) => (
    <div
        className={cn(
            'rounded-r-md border-l-[3px] px-4 py-3 text-sm text-muted-foreground dark:bg-transparent',
            calloutTone[tone],
            className
        )}
        {...props}
    />
)
